import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";

type Theme = "light" | "dark";
const STORAGE_KEY = "horizon.theme";

function getInitialTheme(): Theme {
  if (typeof window === "undefined") return "dark";
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === "light" || stored === "dark") return stored;
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function applyTheme(theme: Theme) {
  document.documentElement.classList.toggle("dark", theme === "dark");
}

export function useTheme(): [Theme, (next: Theme) => void] {
  const [theme, setThemeState] = useState<Theme>(getInitialTheme);

  useEffect(() => {
    applyTheme(theme);
    window.localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  return [theme, setThemeState];
}

export function ThemeToggle() {
  const [theme, setTheme] = useTheme();

  return (
    <div
      role="tablist"
      className="flex items-center rounded-full border border-border-subtle bg-bg-card p-0.5"
    >
      <ToggleButton
        label="Claro"
        icon="☀"
        active={theme === "light"}
        onClick={() => setTheme("light")}
      />
      <ToggleButton
        label="Escuro"
        icon="☾"
        active={theme === "dark"}
        onClick={() => setTheme("dark")}
      />
    </div>
  );
}

function ToggleButton({
  label,
  icon,
  active,
  onClick,
}: {
  label: string;
  icon: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        "flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition",
        active
          ? "bg-brand-amber text-text-on-accent shadow-sm"
          : "text-text-muted hover:text-text-primary"
      )}
    >
      <span aria-hidden className="text-[13px]">{icon}</span>
      <span>{label}</span>
    </button>
  );
}
